import { type ParseKeys, t } from 'i18next'

import { currentEnv } from '@/env/vars'
import { addNotification } from '@/store/gigiChat/Notifications'
import type { TToastStatus } from '@/types/gigiChat/Notifications'
import { handleErrorMessages, POST } from '@/utils/fetch'

const responseFileNameBase = 'New GiGi response'

const errorMapping: Record<string, { severity?: TToastStatus; i18nKey: ParseKeys }> = {
    no_tables_in_document: { severity: 'warning', i18nKey: 'chat.export.error.noTablesInDocument' },
}

const triggerFileDownload = (content: Blob, fileName: string) => {
    const url = URL.createObjectURL(content)

    const a = document.createElement('a')
    a.setAttribute('href', url)
    a.download = fileName

    a.addEventListener(
        'click',
        () => {
            setTimeout(() => {
                // cleanup
                URL.revokeObjectURL(url)
            }, 10_000)

            document.body.removeChild(a)
        },
        { once: true },
    )

    // need the anchor to be present in the body for some browsers
    document.body.appendChild(a)

    a.click()
}

const createRequestHandler = (fileName: string) => async (res: Response | undefined | void) => {
    if (!res) {
        // something is very wrong
        addNotification('error', t('global.error.wrong'))
        return
    }
    if (res.ok) {
        try {
            addNotification('success', t('chat.export.exportSuccessful'))

            const blob = await res.blob()
            triggerFileDownload(blob, fileName)
        } catch (_err) {
            addNotification('error', t('global.error.wrong'))
        }
    } else {
        try {
            const json = (await res.json()) as { error?: string }
            if (json && json.error && errorMapping[json.error]) {
                // custom error message
                addNotification(errorMapping[json.error].severity ?? 'error', t(errorMapping[json.error].i18nKey))
            } else {
                // unknown error - go through default error processing
                throw res
            }
        } catch (_err) {
            // default behevior
            handleErrorMessages(res)
        }
    }
}

const createErrorHandler = () => (error: unknown) => {
    addNotification('error', t('global.error.wrong'))

    throw error
}

export const exportMessageToDocx = ({ message, fileName }: { message: string; fileName?: string }) => {
    const targetFileName = fileName ?? `${responseFileNameBase}.docx`

    return POST(`${currentEnv.utilityUrl}/export/message-to-docx`, { message, fileName }, false, true)
        .then(createRequestHandler(targetFileName))
        .catch(createErrorHandler())
}

export const exportMessageToXlsx = ({ message, fileName }: { message: string; fileName?: string }) => {
    const targetFileName = fileName ?? `${responseFileNameBase}.xlsx`

    return POST(`${currentEnv.utilityUrl}/export/message-to-xlsx`, { message, fileName }, false, true)
        .then(createRequestHandler(targetFileName))
        .catch(createErrorHandler())
}

export const exportMessageToPptx = ({ message, fileName }: { message: string; fileName?: string }) => {
    const targetFileName = fileName ?? `${responseFileNameBase}.pptx`

    return POST(`${currentEnv.utilityUrl}/export/message-to-pptx`, { message, fileName }, false, true)
        .then(createRequestHandler(targetFileName))
        .catch(createErrorHandler())
}
